import * as React from 'react';
import { useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { useTheme } from '../../../theme';
import Link from '../../../Link';
import ResendTicketsForm from '../../../ResendTicketsForm/components/ResendTicketsForm';
import { ResendTicketsStages } from '../../../ResendTicketsForm/types';
import { OrderCardContext } from '../OrderCard';
import { useResendTickets } from '../../../Checkout/components/Checkout/hooks';
import { hideText } from '../../../Passenger/utils';
import CommonItem from '../CommonItem/CommonItem';
import ModalConfirm from '../../../ModalConfirm';
import { getUserValue } from '../../../utils';
import { TravellerFieldEnum } from '@websky/graphql';
import WarningModal from '../../../WarningModal';

interface Props {
	className?: string;
}

const OrderTickets: React.FC<Props> = props => {
	const { t } = useTranslation('OrderCard');
	const { OrderTickets: css } = useTheme('OrderCard');

	const { order } = useContext(OrderCardContext);
	const email = useMemo(() => getUserValue(order.customer, TravellerFieldEnum.Email), [order.id]);
	const {
		resendStage,
		handleWarningModalClose,
		handleResendEmail,
		isModalOpen,
		isUnauthorizedError,
		closeModal,
		openModal
	} = useResendTickets(order.id);
	const submitRef = useRef<() => void>();

	const isLoading = resendStage === ResendTicketsStages.Loading;

	return (
		<>
			<CommonItem
				icon={'Action/Send Mail'}
				text={t(!email ? 'Send tickets to the email?' : 'Tickets have been sent to {{email}}', {
					email: hideText(email)
				})}
				className={cn(css.wrapper, props.className)}
				buttons={
					<Link className={css.link} action={openModal}>
						{t('Resend it')}
					</Link>
				}
			/>

			<ModalConfirm
				isOpen={isModalOpen}
				onClose={closeModal}
				ignoreClose={isLoading}
				title={t('Send all documents to email')}
				cancelBtn={t('Cancel')}
				confirmBtn={t('Send')}
				isLoading={isLoading}
				onConfirm={() => submitRef.current?.()}
				onCancel={closeModal}
			>
				<ResendTicketsForm
					defaultEmail={email}
					onSubmit={({ email }) => handleResendEmail(email)}
					submitRef={submitRef}
				/>
			</ModalConfirm>

			<WarningModal
				isOpen={isUnauthorizedError}
				title={t('Error')}
				content={t('To submit documents, log in to your account or use the email provided during the booking.')}
				buttonText={t('Close')}
				onButtonClick={handleWarningModalClose}
				onClose={handleWarningModalClose}
			/>
		</>
	);
};

export default OrderTickets;
